import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { SessionUser } from '@smile/shared';
import { api } from './api/client';
import { can } from './lib/perms';
import { Navbar } from './components/Navbar';
import { ScreenBoundary } from './components/ScreenBoundary';
import { Dashboard } from './routes/Dashboard';
import { Klienti } from './routes/Klienti';
import { Login } from './routes/Login';
import { Prevozy } from './routes/Prevozy';
import { Rezervace } from './routes/Rezervace';
import { RezervaceDetail } from './routes/RezervaceDetail';
import { RezervaceNova } from './routes/RezervaceNova';
import { Protokol } from './routes/Protokol';
import { Uctenka } from './routes/Uctenka';
import { UzaverkaTisk } from './routes/UzaverkaTisk';
import { Dobropis } from './routes/Dobropis';
import { Nastaveni } from './routes/Nastaveni';
import { Smeny } from './routes/Smeny';
import { Statistiky } from './routes/Statistiky';
import { Uzivatele, UzivatelDetail } from './routes/Uzivatele';
import { Ucet } from './routes/Ucet';
import { Ceniky, CenikDetail } from './routes/Ceniky';
import { Ceny, SezonaDetail } from './routes/Ceny';
import { WhatsAppChaty } from './routes/WhatsAppChaty';
import { Prilety } from './routes/Prilety';
import './styles/app.css';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, err) =>
        // A 401 means the session is gone; retrying just delays the redirect.
        !(err instanceof Error && err.message === 'unauthorized') && count < 2,
    },
  },
});

function Placeholder({ title }: { title: string }) {
  return (
    <div className="container">
      <div className="pagehead">
        <h1>{title}</h1>
      </div>
      <div className="card state">
        Tato obrazovka zatím není součástí fáze 0. Připravujeme ji podle
        schváleného mockupu.
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.me());
    } catch {
      setUser(null);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!checked) {
    return <div className="state">Načítám…</div>;
  }

  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        {user ? (
          <>
            <Navbar user={user} />
            {user.legacyPassword && (
              <div className="container" style={{ paddingBottom: 0 }}>
                <div className="alert warn">
                  Vaše heslo je uloženo zastaralým způsobem. Při nejbližší
                  příležitosti si ho prosím změňte.
                </div>
              </div>
            )}
            <ScreenBoundary>
            <Routes>
              {/* No dashboard grant (a driver): home is Rezervace, the one screen it may read. The legacy
                  Jízdy (a driver's own rides) is not built yet — /transfers/mine has no route (30 Oct 2026). */}
              <Route path="/" element={can(user, '/dashboard/*', 'read') ? <Dashboard /> : <Navigate to="/rezervace" replace />} />
              <Route path="/rezervace" element={<Rezervace />} />
              <Route path="/rezervace/nova" element={<RezervaceNova />} />
              <Route path="/rezervace/:id" element={<RezervaceDetail />} />
              <Route path="/protokol/:id" element={<Protokol />} />
              <Route path="/uctenka/:id" element={<Uctenka />} />
              <Route path="/uzaverka/:id" element={<UzaverkaTisk />} />
              <Route path="/dobropis/:id" element={<Dobropis />} />
              <Route path="/prevozy" element={<Prevozy />} />
              <Route path="/prilety" element={<Prilety />} />
              <Route path="/klienti" element={<Klienti />} />
              <Route path="/smeny" element={<Smeny />} />
              <Route path="/statistiky" element={<Statistiky />} />
              <Route path="/nastaveni" element={<Nastaveni />} />
              <Route path="/nastaveni/ceniky" element={<Ceniky />} />
              <Route path="/nastaveni/ceniky/:id" element={<CenikDetail />} />
              <Route path="/nastaveni/ceny" element={<Ceny />} />
              <Route path="/nastaveni/ceny/sezona/:id" element={<SezonaDetail />} />
              <Route path="/uzivatele" element={<Uzivatele />} />
              <Route path="/uzivatele/:id" element={<UzivatelDetail />} />
              <Route path="/ucet" element={<Ucet />} />
              <Route path="/whatsapp" element={<WhatsAppChaty />} />
              <Route path="/prihlaseni" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Placeholder title="Stránka nenalezena" />} />
            </Routes>
            </ScreenBoundary>
          </>
        ) : (
          <Routes>
            <Route path="/prihlaseni" element={<Login onSuccess={refresh} />} />
            <Route path="*" element={<Navigate to="/prihlaseni" replace />} />
          </Routes>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
}
